import React, { type ReactElement, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StylesClassNames } from '../styles'

interface MinifiedTrackProps {
  currentTime: number
  onSeek: (c: number) => void
  duration: number
  minified: boolean
  isPlaying: boolean
  loading: boolean
  csx?: CustomStyles<StylesClassNames>
  height: number
}

export function MinifiedTrack({
  currentTime,
  onSeek,
  duration,
  loading,
  minified,
  isPlaying,
  height,
  csx,
}: MinifiedTrackProps): ReactElement {
  const styles = getStyles(defaultStyles, csx)

  const showTrack = minified || loading
  const showKnob = isPlaying && !loading

  const [isHover, setIsHover] = useState(false)
  const progressBarRef = useRef<HTMLDivElement>(null)

  const getPercentage = (): string => {
    const percentage = (currentTime / duration) * 100
    return `${percentage}%`
  }

  const getClientX = (event: MouseEvent | TouchEvent): number => {
    if ('touches' in event) {
      // This is a TouchEvent
      return event.touches[0].clientX
    }
    // This is a MouseEvent
    return event.clientX
  }

  const setPosition = (event: MouseEvent | TouchEvent): void => {
    if (!progressBarRef.current) return

    const progressBarRect = progressBarRef.current.getBoundingClientRect()
    const maxRelativePos = progressBarRect.width
    const relativePos = getClientX(event) - progressBarRect.left

    if (relativePos < 0) {
      onSeek(0)
    } else if (relativePos > maxRelativePos) {
      onSeek(duration)
    } else {
      onSeek((duration * relativePos) / maxRelativePos)
    }
  }

  const onMouseOrTouchMove = (event: MouseEvent | TouchEvent): void => {
    setPosition(event)
  }

  const onMouseOrTouchUp = (event: MouseEvent | TouchEvent): void => {
    if (event instanceof MouseEvent) {
      window.removeEventListener('mousemove', onMouseOrTouchMove)
      window.removeEventListener('mouseup', onMouseOrTouchUp)
    } else {
      window.removeEventListener('touchmove', onMouseOrTouchMove)
      window.removeEventListener('touchend', onMouseOrTouchUp)
    }
    setIsHover(false)
  }

  const onMouseDownOrTouchStart = (
    event: React.MouseEvent | React.TouchEvent
  ): void => {
    if (event.nativeEvent instanceof MouseEvent) {
      window.addEventListener('mousemove', onMouseOrTouchMove)
      window.addEventListener('mouseup', onMouseOrTouchUp)
    } else {
      window.addEventListener('touchmove', onMouseOrTouchMove)
      window.addEventListener('touchend', onMouseOrTouchUp)
    }

    setPosition(event.nativeEvent)
    setIsHover(true)
  }

  const handleMouseEnter = (): void => {
    setIsHover(true)
  }

  const handleMouseLeave = (): void => {
    setIsHover(false)
  }

  return (
    <Box
      {...styles('trackBar', {
        pointerEvents: loading ? 'none' : 'auto',
      })}
      tabIndex={0}
      onMouseDown={onMouseDownOrTouchStart}
      onTouchStart={onMouseDownOrTouchStart}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={progressBarRef}
    >
      <Box
        {...styles('trackLine', {
          height: showTrack ? '1px' : height - 8,
          backgroundColor: showTrack ? 'text.secondary' : 'transparent',
        })}
      >
        <Box
          {...styles('trackKnob', {
            opacity: showKnob || isHover ? 1 : 0,
            transition: isHover
              ? 'opacity 0.3s ease-in-out'
              : 'opacity 0.75s ease-in-out',
          })}
          left={getPercentage()}
        />
      </Box>
    </Box>
  )
}
